import styled from 'styled-components';

import {
  Container as BaseContainer,
  RightSideContent as BaseRightSideContent,
  primaryButtonSizing,
} from '../CreatePost/styles';

export {
  LeftSideContent,
  HeaderContainer,
  InputInfoPost,
  Basicnfo,
  InfoPost,
  UserAvatar,
  SubmitHint,
  toolSelectStyles,
  subjectsSelectStyles,
} from '../CreatePost/styles';

/**
 * Empilha as duas colunas no tablet para baixo.
 *
 * A base é `flex-direction: row` e nunca quebra: abaixo de 1024px o formulário
 * já ocupa 100% da largura, então ele e o painel de arquivos passam a disputar
 * a mesma linha e os dois ficam espremidos. Empilhar é o que devolve largura
 * aos campos.
 *
 * O 1024px é o corte que o resto da tela já usa — o mesmo em que o formulário
 * vira 100% e a seção de escolaridade/kit se empilha. Reaproveitar evita uma
 * faixa de largura em que metade da tela já quebrou e a outra metade não.
 */
export const Container = styled(BaseContainer)`
  @media (max-width: 1024px) {
    flex-direction: column;
  }

  /* 2.38rem de cada lado custam 20% da largura de um celular de 375px. */
  @media (max-width: 520px) {
    padding: 1rem;
  }
`;

/**
 * A borda que separa as colunas acompanha a direção em que elas estão.
 *
 * Empilhado, um `border-left` vira um risco vertical solto na lateral do
 * painel; quem separa duas coisas em cima e embaixo é uma linha em cima.
 *
 * O `min-width: 0` fica só deste lado. A coluna direita pede `width: 100%` e,
 * num flex em linha, isso a torna gulosa; sem o zero ela força a soma a passar
 * da largura da tela em vez de ceder espaço. Do lado esquerdo o mesmo zero é
 * destrutivo: o piso implícito do conteúdo é o que impede o formulário de
 * 50rem de ser espremido, e sem ele os campos de largura fixa transbordam por
 * cima deste painel.
 */
export const RightSideContent = styled(BaseRightSideContent)`
  min-width: 0;

  @media (max-width: 1024px) {
    border-left: none;
    border-top: 1px solid #e2e2e2;
    margin-left: 0;
    margin-top: 1.5rem;
    padding: 1.5rem 0 0;
  }
`;

/**
 * Aviso de que escolaridade e kit não mudam aqui.
 *
 * Os dois campos continuam aparecendo como os selects da publicação, só que
 * desabilitados — esconder confundiria mais, porque o aluno acharia que a
 * informação se perdeu. Esta linha diz por quê.
 */
export const LockedFieldsHint = styled.p`
  font-family: 'RedHatDisplay-Regular';
  font-size: 0.8rem;
  color: #63646a;
  margin-top: 0.4rem;
`;

/** Coluna direita, no lugar onde a publicação mostra o desafio. */
export const CurrentMediaPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  > h1 {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-family: 'RedHatDisplay-Bold';
    font-size: 1.5rem;
    color: #323232;
  }

  > p {
    font-family: 'RedHatDisplay-Regular';
    font-size: 0.85rem;
    color: #63646a;
  }

  @media (max-width: 520px) {
    > h1 {
      font-size: 1.25rem;
    }
  }
`;

export const CurrentMediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(7rem, 1fr));
  gap: 0.7rem;

  /* Em 375px, 7rem só deixa caber duas colunas e sobra uma faixa vazia à
     direita; 5.5rem fecha três e a miniatura continua grande o bastante para
     se reconhecer o arquivo. */
  @media (max-width: 520px) {
    grid-template-columns: repeat(auto-fill, minmax(5.5rem, 1fr));
    gap: 0.5rem;
  }
`;

export const MediaThumb = styled.div<{ $removed: boolean }>`
  position: relative;
  aspect-ratio: 1;
  border-radius: 0.6rem;
  overflow: hidden;
  background: #000;
  border: 2px solid ${({ $removed }) => ($removed ? '#e53935' : 'transparent')};

  > img,
  > video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    /* A mídia marcada para sair continua visível, só esmaecida — dá para
       voltar atrás sem ter que adivinhar o que era. */
    opacity: ${({ $removed }) => ($removed ? 0.35 : 1)};
  }
`;

export const MediaBadge = styled.span`
  position: absolute;
  left: 0.35rem;
  bottom: 0.35rem;
  padding: 0.15rem 0.4rem;
  border-radius: 0.3rem;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-family: 'RedHatDisplay-Medium';
  font-size: 0.65rem;
`;

export const MediaToggle = styled.button<{ $removed: boolean }>`
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.8rem;
  line-height: 1;
  color: #fff;
  background: ${({ $removed }) => ($removed ? '#4caf50' : 'rgba(0,0,0,0.6)')};

  &:focus-visible {
    outline: 2px solid #1e88e5;
    outline-offset: 2px;
  }

  /*
   * 1.5rem dá 24px, alvo pequeno demais para o dedo — e aqui errar significa
   * marcar para remoção o arquivo errado. Não vai aos 44px recomendados de
   * propósito: a miniatura tem ~88px de lado no celular, e um botão de 44
   * cobriria metade da imagem que ele deveria deixar ver.
   */
  @media (max-width: 520px) {
    width: 2rem;
    height: 2rem;
    font-size: 1rem;
  }
`;

/**
 * Linha do salvar + cancelar.
 *
 * O PrimaryButton não tem tamanho próprio, e na publicação quem o dimensiona é
 * a regra `InputInfoPost > form > .primary-button`. Aqui ele deixa de ser
 * filho direto do form — está dentro desta linha, ao lado do cancelar — então
 * aquele seletor não o alcança e o botão saía sem padding nenhum. As medidas
 * vêm da mesma origem da publicação, para as duas telas não divergirem.
 */
export const ActionsRow = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: center;
  margin-top: 1.8rem;

  > .primary-button {
    ${primaryButtonSizing}
  }

  /*
   * No celular o salvar ocupa a linha inteira e o cancelar desce para baixo
   * dele, na ordem em que já estão no DOM — ação principal primeiro.
   *
   * A altura fixa precisa sair junto. O rótulo do botão troca conforme o que
   * falta ("O projeto precisa de ao menos um arquivo") e não cabe numa linha
   * em tela estreita; como o PrimaryButton tem overflow: hidden, o texto seria
   * cortado em vez de quebrar. Vira altura mínima com quebra permitida.
   */
  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.4rem;

    /* O form empilha com align-items: flex-start, então esta linha encolhe
       para o próprio conteúdo — sem isto o width: 100% do botão se resolve
       contra a largura encolhida e ele não chega à borda. */
    width: 100%;

    > .primary-button {
      width: 100%;
      height: auto;
      min-height: 3.55rem;
      padding: 0.9rem 1.2rem;
      white-space: normal;
      text-align: center;
      line-height: 1.3;
    }
  }
`;

export const CancelButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'RedHatDisplay-Medium';
  font-size: 0.9rem;
  color: #8c8c8c;

  &:hover {
    color: #323232;
  }

  /* Alvo de toque: um link de texto solto fica pequeno demais para o dedo. */
  @media (max-width: 520px) {
    width: 100%;
    min-height: 2.75rem;
  }
`;

export const ErrorHint = styled.p`
  font-family: 'RedHatDisplay-Medium';
  font-size: 0.85rem;
  color: #e53935;
  margin-top: 0.6rem;
`;
